import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Store, Truck, CreditCard, FileText, Banknote, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatEUR, SHIPPING_COST, FREE_SHIPPING_LIMIT } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const FIELDS = [
  { name: "ime", label: "Ime", placeholder: "Janez", half: true },
  { name: "priimek", label: "Priimek", placeholder: "Novak", half: true },
  { name: "email", label: "E-pošta", placeholder: "janez@primer.si", type: "email" },
  { name: "telefon", label: "Telefon", placeholder: "040 123 456" },
  { name: "naslov", label: "Ulica in hišna številka", placeholder: "Industrijska cesta 12" },
  { name: "posta", label: "Poštna številka", placeholder: "1000", half: true },
  { name: "kraj", label: "Kraj", placeholder: "Ljubljana", half: true },
];

export default function CheckoutPage() {
  const { items, total, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    ime: "", priimek: "", email: "", telefon: "",
    naslov: "", posta: "", kraj: "", podjetje: "", opombe: "",
  });
  const [dostava, setDostava] = useState("prevzem");
  const [placilo, setPlacilo] = useState("predracun");
  const [submitting, setSubmitting] = useState(false);

  const shipping = dostava === "dostava" && total < FREE_SHIPPING_LIMIT ? SHIPPING_COST : 0;
  const grandTotal = total + shipping;

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        customer: form,
        dostava,
        placilo,
        items: items.map((i) => ({
          product_id: i.key ? null : i.id,
          name: i.name,
          price: i.price,
          qty: i.qty,
          unit: i.unit || "kos",
          image: i.image || null,
          meta: i.meta || null,
        })),
      };
      const res = await api.post("/orders", payload);
      clear();
      navigate(`/narocilo/${res.data.id}`);
    } catch (err) {
      toast.error("Naročila ni bilo mogoče oddati", {
        description: err.response?.data?.detail || "Poskusite znova.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center" data-testid="checkout-empty">
        <h1 className="font-display text-2xl font-bold text-[#1A1D1A]">Vaša košarica je prazna</h1>
        <p className="mt-2 text-stone-500">Pred blagajno dodajte izdelke v košarico.</p>
        <Link to="/trgovina" data-testid="checkout-empty-browse-btn" className="mt-6 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#2D4030] text-[#FBF9F5] font-semibold hover:bg-[#1E2C20] transition-colors">
          V trgovino <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="checkout-page">
      <p className="font-tech text-xs uppercase tracking-[0.2em] text-[#B45309] mb-2">Blagajna</p>
      <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1D1A] mb-10">Zaključite naročilo</h1>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7 space-y-6">
          <section className="bg-white rounded-2xl border border-[#E4E0D7] p-6 sm:p-8" data-testid="checkout-customer-section">
            <h2 className="font-display font-bold text-lg text-[#1A1D1A] mb-5">Podatki za dostavo</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.half ? "" : "sm:col-span-2"}>
                  <label className="text-xs font-tech uppercase tracking-wider text-stone-500 block mb-1.5">{f.label} *</label>
                  <input
                    data-testid={`checkout-input-${f.name}`}
                    type={f.type || "text"}
                    required
                    value={form[f.name]}
                    onChange={(e) => setField(f.name, e.target.value)}
                    placeholder={f.placeholder}
                    className="w-full h-11 px-4 rounded-lg bg-[#FBF9F5] border border-[#E4E0D7] text-sm focus:outline-none focus:ring-2 focus:ring-[#2D4030]/30"
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="text-xs font-tech uppercase tracking-wider text-stone-500 block mb-1.5">Podjetje (neobvezno)</label>
                <input
                  data-testid="checkout-input-podjetje"
                  value={form.podjetje}
                  onChange={(e) => setField("podjetje", e.target.value)}
                  placeholder="Ime podjetja"
                  className="w-full h-11 px-4 rounded-lg bg-[#FBF9F5] border border-[#E4E0D7] text-sm focus:outline-none focus:ring-2 focus:ring-[#2D4030]/30"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-tech uppercase tracking-wider text-stone-500 block mb-1.5">Opombe (neobvezno)</label>
                <textarea
                  data-testid="checkout-input-opombe"
                  value={form.opombe}
                  onChange={(e) => setField("opombe", e.target.value)}
                  rows={3}
                  placeholder="Posebne želje glede dostave ali razreza …"
                  className="w-full px-4 py-3 rounded-lg bg-[#FBF9F5] border border-[#E4E0D7] text-sm focus:outline-none focus:ring-2 focus:ring-[#2D4030]/30 resize-none"
                />
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-[#E4E0D7] p-6 sm:p-8" data-testid="checkout-delivery-section">
            <h2 className="font-display font-bold text-lg text-[#1A1D1A] mb-5">Način prevzema</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <OptionCard
                testid="delivery-prevzem"
                active={dostava === "prevzem"}
                onClick={() => setDostava("prevzem")}
                icon={Store}
                title="Osebni prevzem"
                desc="Industrijska cesta 12, Ljubljana"
                price="Brezplačno"
              />
              <OptionCard
                testid="delivery-dostava"
                active={dostava === "dostava"}
                onClick={() => setDostava("dostava")}
                icon={Truck}
                title="Dostava na dom"
                desc={total >= FREE_SHIPPING_LIMIT ? "Brezplačna nad 500 €" : `Po vsej Sloveniji`}
                price={shipping === 0 ? "Brezplačno" : formatEUR(SHIPPING_COST)}
              />
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-[#E4E0D7] p-6 sm:p-8" data-testid="checkout-payment-section">
            <h2 className="font-display font-bold text-lg text-[#1A1D1A] mb-5">Način plačila</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <OptionCard
                testid="payment-predracun"
                active={placilo === "predracun"}
                onClick={() => setPlacilo("predracun")}
                icon={FileText}
                title="Predračun"
                desc="Plačilo po ponudbi"
              />
              <OptionCard
                testid="payment-povzetje"
                active={placilo === "povzetje"}
                onClick={() => setPlacilo("povzetje")}
                icon={Banknote}
                title="Po povzetju"
                desc="Ob dostavi / prevzemu"
              />
              <OptionCard
                testid="payment-kartica"
                active={placilo === "kartica"}
                onClick={() => setPlacilo("kartica")}
                icon={CreditCard}
                title="Kartica"
                desc="Ob prevzemu"
              />
            </div>
          </section>
        </div>

        <aside className="lg:col-span-5 bg-white rounded-2xl border border-[#E4E0D7] p-6 sm:p-8 lg:sticky lg:top-32" data-testid="checkout-summary">
          <h2 className="font-display font-bold text-lg text-[#1A1D1A] mb-5">Povzetek naročila</h2>
          <ul className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {items.map((i) => {
              const key = i.key || i.id;
              return (
                <li key={key} className="flex gap-3 items-center" data-testid={`checkout-item-${key}`}>
                  {i.image && <img src={i.image} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#1A1D1A] line-clamp-1">{i.name}</p>
                    <p className="text-[11px] text-stone-500">{i.qty} × {formatEUR(i.price)}</p>
                  </div>
                  <span className="font-tech font-semibold text-sm">{formatEUR(i.price * i.qty)}</span>
                </li>
              );
            })}
          </ul>
          <div className="mt-5 pt-5 border-t border-[#E4E0D7] space-y-2.5 text-sm">
            <div className="flex justify-between text-stone-600">
              <span>Vmesna vsota</span>
              <span className="font-tech" data-testid="checkout-subtotal">{formatEUR(total)}</span>
            </div>
            <div className="flex justify-between text-stone-600">
              <span>Dostava</span>
              <span className="font-tech" data-testid="checkout-shipping">{shipping === 0 ? "Brezplačno" : formatEUR(shipping)}</span>
            </div>
            <div className="flex justify-between font-display font-bold text-lg pt-3 border-t border-[#E4E0D7]">
              <span>Skupaj z DDV</span>
              <span className="font-tech text-[#B45309]" data-testid="checkout-total">{formatEUR(grandTotal)}</span>
            </div>
          </div>
          <button
            data-testid="checkout-submit-button"
            type="submit"
            disabled={submitting}
            className="mt-6 w-full h-12 rounded-full bg-[#2D4030] text-[#FBF9F5] font-semibold hover:bg-[#1E2C20] transition-colors disabled:opacity-60"
          >
            {submitting ? "Oddajam naročilo …" : "Potrdi naročilo"}
          </button>
          <p className="mt-3 text-[11px] text-stone-400 text-center">
            S potrditvijo naročila se strinjate s splošnimi pogoji poslovanja.
          </p>
        </aside>
      </form>
    </div>
  );
}

function OptionCard({ active, onClick, icon: Icon, title, desc, price, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-xl border-2 p-4 transition-colors ${
        active ? "border-[#2D4030] bg-[#2D4030]/5" : "border-[#E4E0D7] hover:border-[#2D4030]/40"
      }`}
    >
      <div className="flex items-center justify-between">
        <Icon className={`w-5 h-5 ${active ? "text-[#2D4030]" : "text-stone-400"}`} />
        {price && <span className="text-xs font-tech font-bold text-[#B45309]">{price}</span>}
      </div>
      <p className="mt-2.5 text-sm font-semibold text-[#1A1D1A]">{title}</p>
      <p className="text-xs text-stone-500 mt-0.5">{desc}</p>
    </button>
  );
}
